import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const categories = [
  { id: "general", label: "Général" },
  { id: "branding", label: "Branding" },
  { id: "site", label: "Site web" },
  { id: "abonnement", label: "Abonnement" },
];

// Each FAQ item: { q, a, cta?: { label, type } }
// cta.type: "quote" (open QuoteDialog) | "project" (scroll to accompagnement then open quote)
const faqs = {
  general: [
    {
      q: "Est-ce que vous vous occupez de tout ?",
      a: "Oui, c'est même notre cœur de métier. Avec l'Accompagnement complet (à partir de 3490 € TTC), nous prenons en charge chaque étape : positionnement, branding, visuels, création du site, stratégie de communication, lancement et acquisition des premiers clients. Vous avancez avec un seul interlocuteur, un plan clair et zéro perte de temps.",
      cta: { label: "Être accompagné", type: "project" },
    },
    {
      q: "Pourquoi les prix sont indiqués « à partir de » ?",
      a: "Parce que chaque projet est unique. Le prix de base correspond à la prestation minimum (complexité standard, délais confortables, 1 marque). Il évolue selon le volume, le niveau de personnalisation, l'urgence et les spécificités (multi-langues, fonctionnalités avancées, intégrations). Vous recevez toujours un devis détaillé et figé avant toute commande — aucune mauvaise surprise.",
      cta: { label: "Demander un devis", type: "quote" },
    },
    {
      q: "Quels sont les délais moyens de réalisation ?",
      a: "Cela dépend de la prestation : 24 à 72h pour un visuel unique, 3 à 7 jours pour un pack. Pour un logo complet : 5 à 10 jours. Pour un site web : 2 à 6 semaines. Pour un accompagnement complet, nous construisons un calendrier sur-mesure au démarrage.",
    },
    {
      q: "Comment se passe le paiement ?",
      a: "Pour les prestations ponctuelles : acompte de 50% à la signature, solde à la livraison. Pour les sites web : achat comptant, paiement en plusieurs fois (2, 3 ou 4x sans frais) ou location mensuelle. Nos abonnements visuels sont prélevés automatiquement chaque mois via Stripe, sans engagement.",
    },
    {
      q: "Quels types de clients accompagnez-vous ?",
      a: "Indépendants, PME, start-ups et marques établies — en France, à Maurice, à la Réunion et à l'international. Nous adaptons notre approche à chaque secteur d'activité et à chaque stade de maturité du business.",
    },
    {
      q: "Comment se passe le premier contact ?",
      a: "Très simple : vous nous envoyez une demande via le formulaire de devis ou le chatbot. Nous revenons vers vous sous 24h pour un premier échange (téléphone, WhatsApp ou visio), puis nous vous envoyons un devis précis et un calendrier.",
      cta: { label: "Démarrer maintenant", type: "quote" },
    },
  ],
  branding: [
    {
      q: "Quelle est la différence entre un logo, un branding et une identité de marque ?",
      a: "Le Pack Logo Essentiel (699 € TTC) couvre votre logo + ses déclinaisons. Le Pack Branding Complet (dès 1599 € TTC) inclut logo, palette couleurs, typographies, charte graphique et univers visuel. Le Pack Visibilité (dès 2199 € TTC) ajoute 4 visuels et la mise en place de vos réseaux sociaux pour un lancement complet.",
    },
    {
      q: "Combien de modifications sont incluses ?",
      a: "1 révision incluse sur le visuel unique, 2 sur le pack 2 visuels, révisions illimitées sur le Pack 4 visuels et le Pack Branding Complet — jusqu'à votre satisfaction totale.",
    },
    {
      q: "Mon identité visuelle sera-t-elle vraiment unique ?",
      a: "Oui. Chaque projet commence par un brief approfondi (cible, valeurs, positionnement). Nous ne partons jamais de templates : tout est conçu sur-mesure pour refléter votre marque et la démarquer de la concurrence.",
      cta: { label: "Lancer mon projet", type: "project" },
    },
    {
      q: "Livrez-vous des fichiers sources ?",
      a: "Oui. Vous recevez tous les fichiers nécessaires : HD web, print, sources vectorielles (.ai, .svg) et déclinaisons couleurs. Vous êtes pleinement propriétaire de votre identité.",
    },
  ],
  site: [
    {
      q: "Quelle est la différence entre achat, paiement en plusieurs fois et location ?",
      a: "L'achat (dès 1490 € TTC) vous rend propriétaire du site, livraison clé en main. Le paiement en plusieurs fois (dès 3x 550 € TTC) = même prestation, accès dès le 1er paiement. L'Offre Loueur (dès 180 € TTC / mois) inclut création, hébergement, maintenance et accompagnement — idéal pour lancer une activité de location sans investissement initial (abonnement mensuel sécurisé Stripe).",
    },
    {
      q: "Que se passe-t-il si je choisis la location et que je veux partir ?",
      a: "Après les 12 mois d'engagement minimum, vous pouvez résilier avec 30 jours de préavis. Le code source reste notre propriété mais nous vous aidons à migrer si vous souhaitez racheter le site au tarif d'un achat classique, en déduisant les mensualités déjà versées.",
    },
    {
      q: "Mon site sera-t-il optimisé pour le mobile et le SEO ?",
      a: "Absolument. Tous nos sites sont responsive (mobile, tablette, desktop), rapides et pensés pour le référencement naturel dès la conception : structure HTML propre, meta tags, performance, données structurées et sitemap.",
    },
    {
      q: "J'ai déjà un site qui ne convertit pas. Que faire ?",
      a: "Commencez par un audit (dès 190 € TTC) : nous analysons design, UX, conversion et SEO et vous livrons un rapport concret. Bonne nouvelle : si vous nous confiez ensuite la refonte, l'audit est 100% remboursé.",
      cta: { label: "Demander un audit", type: "quote" },
    },
    {
      q: "Puis-je modifier mon site moi-même après la livraison ?",
      a: "Oui. Nous vous fournissons un back-office simple pour mettre à jour vos textes, images et articles. Pour toute évolution majeure (nouvelle page, fonctionnalité), nous restons disponibles sur devis ou via l'accompagnement.",
    },
  ],
  abonnement: [
    {
      q: "Pourquoi un abonnement plutôt qu'un paiement à l'unité ?",
      a: "La régularité est ce qui fait réellement grandir une marque. Un visuel unique a peu d'impact ; une présence mensuelle cohérente transforme votre audience en clients. Nos abonnements (dès 450 € TTC / mois) assurent cette cadence avec un budget prévisible.",
    },
    {
      q: "Quelles sont les différences entre Démarreur, Pro et Premium ?",
      a: "Démarreur 450 € TTC / mois : 3 posts + accompagnement léger. Pro 750 € TTC / mois (le plus choisi) : 6 posts + gestion réseaux + accompagnement. Premium 1200 € TTC / mois : 8 visuels + stratégie complète. Chaque formule est ajustable selon vos besoins réels.",
    },
    {
      q: "Peut-on résilier à tout moment ?",
      a: "Oui, tous nos abonnements visuels (Démarreur, Pro, Premium) sont sans engagement. Un simple email à contact@emgroupdigital.com avec 30 jours de préavis suffit. Aucune pénalité, aucun frais caché.",
    },
    {
      q: "Puis-je changer de formule en cours d'abonnement ?",
      a: "Bien sûr. Vous pouvez monter ou descendre en gamme à tout moment. Le nouveau tarif s'applique dès le mois suivant.",
    },
    {
      q: "Que se passe-t-il si j'ai pris un Accompagnement complet ?",
      a: "Après votre accompagnement, vous bénéficiez d'un tarif préférentiel de -20% à vie sur nos formules d'abonnement. Un vrai avantage pour maintenir la dynamique après le lancement.",
      cta: { label: "Être accompagné", type: "project" },
    },
  ],
};

export default function Faq({ onOpenQuote = () => {} }) {
  const [cat, setCat] = useState("general");
  const list = faqs[cat] || [];

  const handleCta = (type) => {
    if (type === "project") {
      const el = document.querySelector("#accompagnement");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    onOpenQuote();
  };

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="relative py-24 md:py-32 section-light"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="mb-10 md:mb-14 text-center"
        >
          <span className="text-xs uppercase tracking-[0.25em] text-[#E52431]">
            — FAQ
          </span>
          <h2 className="mt-4 font-outfit text-4xl md:text-5xl font-light tracking-tight text-[#0A0A0A]">
            Les questions <span className="gradient-text font-medium">fréquentes</span>
          </h2>
          <p className="mt-5 text-[#0A0A0A]/60 text-base md:text-lg font-light max-w-2xl mx-auto">
            Délais, tarifs, modalités — tout ce qu&rsquo;il faut savoir avant
            de lancer votre projet.
          </p>
        </motion.div>

        {/* Category tabs */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-10">
          {categories.map((c) => (
            <button
              key={c.id}
              data-testid={`faq-cat-${c.id}`}
              onClick={() => setCat(c.id)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium tracking-wide transition-all duration-300 ${
                cat === c.id
                  ? "gradient-bg text-white shadow-[0_8px_25px_rgba(229,36,49,0.35)]"
                  : "bg-white border border-[#0A0A0A]/10 text-[#0A0A0A]/70 hover:text-[#0A0A0A] hover:border-[#0A0A0A]/25"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Accordion */}
        <div className="light-card rounded-3xl px-6 md:px-10 py-4">
          <AnimatePresence mode="wait">
            <motion.div
              key={cat}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.35 }}
            >
              <Accordion type="single" collapsible defaultValue="item-0" className="w-full">
                {list.map((f, i) => (
                  <AccordionItem
                    key={`${cat}-${i}`}
                    value={`item-${i}`}
                    data-testid={`faq-item-${cat}-${i}`}
                    className="border-b border-[#0A0A0A]/10 last:border-0"
                  >
                    <AccordionTrigger className="text-left font-outfit text-base md:text-lg font-medium text-[#0A0A0A] hover:text-[#E52431] py-6 hover:no-underline">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[#0A0A0A]/70 pb-6 text-base font-light leading-relaxed">
                      <p>{f.a}</p>
                      {f.cta && (
                        <button
                          onClick={() => handleCta(f.cta.type)}
                          data-testid={`faq-cta-${cat}-${i}`}
                          className="mt-5 inline-flex items-center gap-2 rounded-full gradient-bg text-white font-semibold px-6 py-3 text-xs uppercase tracking-[0.15em] hover:shadow-[0_10px_30px_rgba(229,36,49,0.35)] hover:scale-[1.02] transition-all duration-300 btn-shine"
                        >
                          {f.cta.label}
                          <ArrowRight size={14} />
                        </button>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="mt-12 rounded-3xl bg-gradient-to-br from-[#0A0A0A] to-[#141418] p-10 md:p-12 text-center relative overflow-hidden"
        >
          <div className="absolute -top-20 -left-20 w-[300px] h-[300px] rounded-full bg-[#E52431]/25 blur-[100px]" />
          <div className="absolute -bottom-20 -right-20 w-[300px] h-[300px] rounded-full bg-[#1F2C6E]/30 blur-[100px]" />

          <div className="relative z-10">
            <h3 className="font-outfit text-3xl md:text-4xl font-light tracking-tight text-white">
              Vous avez encore{" "}
              <span className="gradient-text font-medium">une question</span> ?
            </h3>
            <p className="mt-4 text-white/65 font-light max-w-xl mx-auto">
              Un échange rapide vaut souvent mieux qu&rsquo;une longue FAQ. Parlons
              de votre projet directement.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                data-testid="faq-bottom-project"
                onClick={() => handleCta("project")}
                className="inline-flex items-center gap-2 rounded-full gradient-bg text-white font-semibold px-8 py-4 text-sm uppercase tracking-[0.15em] hover:shadow-[0_0_50px_rgba(229,36,49,0.5)] hover:scale-[1.02] transition-all duration-300 btn-shine"
              >
                Lancer mon projet
                <ArrowRight size={16} />
              </button>
              <button
                data-testid="faq-bottom-quote"
                onClick={() => onOpenQuote()}
                className="inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/15 text-white font-semibold px-8 py-4 text-sm uppercase tracking-[0.15em] hover:bg-white/10 transition-all duration-300"
              >
                Demander un devis
              </button>
            </div>
            <p className="mt-5 text-xs text-white/45 font-light">
              ⚡ Réponse sous 24h · Maurice · Réunion · France
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
